import { useEffect, useMemo, useState } from 'react';
import {
  listAllActivities,
  createActivity,
  deleteActivity,
  saveDailyEntry,
} from '../api/core.js';
import { usePolicy } from '../components/PolicyContext.jsx';

// Activities view. Lists the makler's logged contacts and meetings, and lets
// them log a new one. Activity types remain fixed; sources come from policy:
// source_sphere values and labels are policy catalogue data.
export default function Activities() {
  const { policy } = usePolicy();
  const allSources = useMemo(
    () => Array.isArray(policy?.catalogues?.sources) ? policy.catalogues.sources : [],
    [policy]
  );
  const activeSources = useMemo(
    () => allSources.filter((source) => source.active !== false),
    [allSources]
  );
  const sourceNames = useMemo(
    () => Object.fromEntries(allSources.map((source) => [source.code, source.name || source.code])),
    [allSources]
  );
  const activeDealTypes = useMemo(() => (policy.catalogues?.dealTypes || []).filter((type) =>
    type.active !== false && ['sale', 'rental'].includes(type.code)), [policy]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    activity_type: 'meeting',
    source_sphere: 'sphere_10',
    contact_name: '',
    contact_phone: '',
    company: '',
    status: 'scheduled',
    date_performed: '',
    meeting_id: '',
    price_czk: '',
    deal_type: 'Prodej',
  });
  const [submitting, setSubmitting] = useState(false);
  const [entryDate, setEntryDate] = useState('');
  const [entryMessage, setEntryMessage] = useState('');
  const isAcquisition = form.activity_type === 'demo';
  const meetings = items.filter((a) => a.activity_type === 'meeting' && a.status === 'completed' &&
    (!form.date_performed || String(a.date_performed).slice(0, 10) <= form.date_performed));
  useEffect(() => {
    if (isAcquisition && activeDealTypes.length &&
        !activeDealTypes.some((type) => (type.code === 'rental' ? 'Nájem' : 'Prodej') === form.deal_type)) {
      setForm((prev) => ({ ...prev, deal_type: activeDealTypes[0].code === 'rental' ? 'Nájem' : 'Prodej' }));
    }
  }, [isAcquisition, activeDealTypes, form.deal_type]);

  const handleSaveDay = async (event) => {
    event.preventDefault();
    setError('');
    setEntryMessage('');
    setSubmitting(true);
    try {
      await saveDailyEntry(entryDate);
      setEntryMessage(`Den ${entryDate} je uložený jako zapsaný, i když neobsahuje žádné aktivity.`);
    } catch (err) {
      setError(err.message || 'Den se nepodařilo uložit.');
    } finally { setSubmitting(false); }
  };

  useEffect(() => {
    if (activeSources.length && !activeSources.some((source) => source.code === form.source_sphere)) {
      setForm((prev) => ({ ...prev, source_sphere: activeSources[0].code }));
    }
  }, [activeSources, form.source_sphere]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await listAllActivities());
    } catch (err) {
      setError(err.message || 'Aktivity se nepodařilo načíst');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      let payload = { ...form };
      delete payload.meeting_id;
      delete payload.price_czk;
      if (isAcquisition) {
        if (!activeDealTypes.length) throw new Error('Není dostupný žádný typ zakázky.');
        const meeting = meetings.find((a) => a.id === Number(form.meeting_id));
        if (!meeting) throw new Error('Vyber schůzku, ze které vznikl nábor.');
        const price = form.price_czk.trim().replace(/\s/g, '').replace(',', '.');
        const amount = price ? Number(price) : null;
        if (amount !== null && (!Number.isFinite(amount) || amount <= 0 ||
            amount > 999999999999.99 || Number(amount.toFixed(2)) !== amount)) {
          throw new Error('Zadej kladnou cenu, nejvýše na dvě desetinná místa.');
        }
        payload = { ...payload, meeting_id: meeting.id, source_sphere: meeting.source_sphere,
          contact_name: meeting.contact_name, contact_phone: meeting.contact_phone,
          company: meeting.company, price_czk: amount, status: 'completed' };
      }
      await createActivity(payload);
      setForm({
        activity_type: 'meeting',
        source_sphere: activeSources[0]?.code || '',
        contact_name: '',
        contact_phone: '',
        company: '',
        status: 'scheduled',
        date_performed: '',
        meeting_id: '',
        price_czk: '',
        deal_type: 'Prodej',
      });
      await load();
    } catch (err) {
      setError(err.message || 'Aktitu se nepodařilo zapsat');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Odmazat tuto aktivitu?')) return;
    try {
      await deleteActivity(id);
      setItems((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setError(err.message || 'Aktitu se nepodarilo odmazat');
    }
  };

  const typeLabel = (value) =>
    ({ call: 'Volání', meeting: 'Schůzka', demo: 'Nábor', cold_call: 'Cold call', door_knocking: 'Osobní návštěva' })[
      value
    ] || value;

  const sphereLabel = (value) => sourceNames[value] || value || '—';

  const statusLabel = (value) =>
    ({ scheduled: 'Naplánováno', completed: 'Dokončeno', failed: 'Nezdářeno', cancelled: 'Zrušeno' })[value] || value;

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <h1 className="dashboard__title">Mé aktivity</h1>
      </div>

      {error && <p className="dashboard__error" role="alert">{error}</p>}

      <p className="dashboard__lead">
        Zapište dnešní kontakty a schůzky. Pravidelný zápis je záklou všeho.
      </p>

      <article className="card">
        <h2 className="card__title">Potvrdit zápis dne (i nulového)</h2>
        <form className="auth__form" onSubmit={handleSaveDay}>
          <label className="auth__label">
            Datum
            <input className="auth__input" type="date" required value={entryDate}
              onChange={(event) => setEntryDate(event.target.value)} />
          </label>
          <p>Potvrzení nemění počty aktivit. Uložený nulový den není chybějící zápis.</p>
          <button className="button button--spot" disabled={submitting} type="submit">Uložit den</button>
        </form>
        {entryMessage && <p role="status">{entryMessage}</p>}
      </article>

      {/* New activity form */}
      <article className="card" style={{ marginBottom: 'var(--cs-s10)' }}>
        <h2 className="card__title">Nová aktivita</h2>
        <form className="auth__form" onSubmit={handleSubmit}>
          <label className="auth__label">
            Typ
            <select className="auth__input" value={form.activity_type} onChange={update('activity_type')}>
              <option value="meeting">Schůzka</option>
              <option value="call">Volání</option>
              <option value="demo">Nábor ze schůzky</option>
              <option value="cold_call">Cold call</option>
              <option value="door_knocking">Osobní návštěva</option>
            </select>
          </label>
          {isAcquisition && <>
            <label className="auth__label">
              Schůzka, ze které vznikl nábor
              <select className="auth__input" required value={form.meeting_id} onChange={update('meeting_id')}>
                <option value="">Vyber schůzku</option>
                {meetings.map((meeting) => <option key={meeting.id} value={meeting.id}>
                  {meeting.contact_name || `Schůzka #${meeting.id}`} · {new Date(meeting.date_performed).toLocaleDateString('cs-CZ')}
                </option>)}
              </select>
            </label>
            <label className="auth__label">
              Typ zakázky
              <select className="auth__input" value={form.deal_type} onChange={update('deal_type')}>
                {activeDealTypes.map((type) =>
                  <option key={type.code} value={type.code === 'rental' ? 'Nájem' : 'Prodej'}>{type.name}</option>)}
              </select>
            </label>
            {!activeDealTypes.length && <p role="alert">Není dostupný žádný typ zakázky.</p>}
            <label className="auth__label">
              {form.deal_type === 'Nájem' ? 'Měsíční nájem (Kč)' : 'Cena nemovitosti (Kč)'}
              <input className="auth__input" inputMode="decimal" value={form.price_czk}
                onChange={update('price_czk')} />
              <small>Nepovinné. Nejde o provizi.</small>
            </label>
          </>}
          {!isAcquisition && <>
          {!activeSources.length && (
            <p className="dashboard__status">Není dostupný žádný aktivní zdroj.</p>
          )}

          <label className="auth__label">
            Zdroj
            <select
              className="auth__input"
              value={form.source_sphere}
              onChange={update('source_sphere')}
              required
              disabled={!activeSources.length}
            >
              {activeSources.map((source) => (
                <option key={source.code} value={source.code}>{source.name || source.code}</option>
              ))}
            </select>
          </label>

          <label className="auth__label">
            Jméno klienta
            <input
              className="auth__input"
              value={form.contact_name}
              onChange={update('contact_name')}
              placeholder="Např. Jan Novák"
            />
          </label>

          <label className="auth__label">
            Telefon
            <input
              className="auth__input"
              value={form.contact_phone}
              onChange={update('contact_phone')}
              placeholder="+420 ..."
            />
          </label>

          <label className="auth__label">
            Společnost
            <input
              className="auth__input"
              value={form.company}
              onChange={update('company')}
            />
          </label>

          </>}
          <label className="auth__label">
            Datum konání
            <input
              className="auth__input"
              type="date"
              required
              value={form.date_performed}
              onChange={update('date_performed')}
            />
          </label>

          <button className="button button--spot" type="submit"
            disabled={submitting || (isAcquisition
              ? loading || !meetings.length || !activeDealTypes.length : !activeSources.length)}>
            {submitting ? 'Zapisuji…' : 'Zapsat aktivitu'}
          </button>
        </form>
      </article>

      {/* Activity list */}
      <h2 style={{ fontSize: 'var(--cs-fs-h4)', marginBottom: 'var(--cs-s6)' }}>Naposledy zapsané</h2>

      {loading ? (
        <p className="dashboard__status">Načítá se…</p>
      ) : items.length === 0 ? (
        <p className="dashboard__status">Zatím žádné aktivity.</p>
      ) : (
        <ul className="activities__list">
          {items.map((a) => (
            <li key={a.id} className="activities__item">
              <div className="activities__meta">
                <span className="activities__type">{typeLabel(a.activity_type)}</span>
                <span className="activities__sphere">{sphereLabel(a.source_sphere)}</span>
              </div>
              <div className="activities__details">
                {a.contact_name && <span>{a.contact_name}</span>}
                {a.company && <span className="activities__company">{a.company}</span>}
                {a.price_czk != null && <span>{Number(a.price_czk).toLocaleString('cs-CZ')} Kč
                  {a.deal_type === 'Nájem' ? ' / měsíc' : ''}</span>}
                {a.meeting_id && <span>Ze schůzky #{a.meeting_id}</span>}
                {a.date_performed && (
                  <span className="activities__date">{new Date(a.date_performed).toLocaleDateString('cs-CZ')}</span>
                )}
              </div>
              <div className="activities__foot">
                <span className={`activities__status activities__status--${a.status}`}>{statusLabel(a.status)}</span>
                <button
                  className="button button--quiet"
                  onClick={() => handleDelete(a.id)}
                  aria-label="Odmazat aktivitu"
                >
                  Odmazat
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
